'use client'

// React Imports
import { useState, useEffect, useMemo, useCallback } from 'react'

// MUI Imports
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import InputAdornment from '@mui/material/InputAdornment'
import { Select, FormControl, InputLabel } from '@mui/material'

// Third-party Imports
import { Icon } from '@iconify/react'
import axios from 'axios'

// Component Imports
import PremiumModuleWrapper from '../common/PremiumModuleWrapper'
import CustomerLedgerTable from './CustomerLedgerTable'
import CustomerLedgerCard from './CustomerLedgerCard'
import Loader from '../../../../../components/Loader/Loader'
import HelpPage from '../../../../../components/common/HelpPage/HelpPage'
import { HelpCircle } from 'lucide-react'
import '../../../../../components/common/SubHeader/SubHeader.css'
import SubHeader from '../../../../../components/common/SubHeader/SubHeader'

// Config/Util Imports
import API_ENDPOINTS, { buildUrl } from '../../../../../config/apiEndpoints'
import { getSession } from '../../../../../getSession'
import { getCustomerLedgerSummaryList, getCurrencyListByUserID } from '../../../../../utils/lead'
import { useModulePermissions } from '../../../../../hooks/useModulePermissions'
import $ from 'jquery'
import 'jquery-table2excel/dist/jquery.table2excel.min.js'

const CustomerLedgerWrapper = ({ defaultView = 'table' }) => {
    // States
    const [viewType, setViewType] = useState(defaultView)
    const [data, setData] = useState([])
    const [currencies, setCurrencies] = useState([])
    const [loading, setLoading] = useState(true)
    const [totalRecords, setTotalRecords] = useState(0)
    const [currentPage, setCurrentPage] = useState(1)
    const [itemsPerPage, setItemsPerPage] = useState(20)
    const [searchTerm, setSearchTerm] = useState('')
    const [selectedCurrencyId, setSelectedCurrencyId] = useState('')
    const [hasFetched, setHasFetched] = useState(false)
    const [isHelpOpen, setIsHelpOpen] = useState(false)
    const [showHelpTooltip, setShowHelpTooltip] = useState(true)

    // ── Module Permissions (PAG10274 = Customer Ledger) ────────────────────────
    const { canSearch, canExport, canView, PermissionDeniedComponent } = useModulePermissions('PAG10274');

    // Helper to get Hidden Field values (if they exist in window or document)
    const getHdnValue = (id) => {
        const el = document.querySelector(`[id$=${id}]`);
        return el ? el.value : '';
    }

    const { userId, parentId, TokenId } = getSession();

    // Fetch Currencies
    const fetchCurrencies = useCallback(async () => {
        try {
            const response = await getCurrencyListByUserID(parseFloat(userId) || 0);
            if (response && (response.Details || response.d)) {
                const currencyData = response.Details || response.d;
                setCurrencies(currencyData);

                // Default to Indian Rupees if not already set
                if (!selectedCurrencyId && currencyData.length > 0) {
                    const inrs = currencyData.filter(c => c.currencyCode?.toUpperCase() === 'INR');
                    // Prefer the INR that has the correct Rupee symbol over junk DB duplicates
                    let inr = inrs.find(c => c.currencySymbol === '₹') || inrs[0];
                    
                    if (!inr) {
                        inr = currencyData.find(c =>
                            c.currencyName?.toLowerCase().includes('rupee') ||
                            c.currencyid === 1 || c.currencyid === "1"
                        );
                    }
                    
                    if (inr) {
                        setSelectedCurrencyId(inr.currencyid);
                    } else {
                        setSelectedCurrencyId(0); // Fallback to All Currencies
                    }
                }
            }
        } catch (error) {
            console.error('Error fetching currencies:', error);
        }
    }, [userId, selectedCurrencyId]);

    // Fetch Ledger Data
    const fetchLedgerData = useCallback(async () => {
        if (selectedCurrencyId === '') return;
        
        setLoading(true);
        try {
            const params = {
                Token: TokenId,
                Details: JSON.stringify({
                    UserId: userId,
                    FilterText: searchTerm,
                    CurrencyId: selectedCurrencyId || 0,
                    OperationMode: "",
                    OffsetValue: (currentPage - 1) * itemsPerPage,
                    PagingSize: itemsPerPage
                })
            };

            const response = await getCustomerLedgerSummaryList(params);

            if (response && response.Status === 1) {
                const details = response.Details;
                if (Array.isArray(details)) {
                    setData(details);
                    if (details.length > 0) {
                        setTotalRecords(details[0].TotalRow || 0);
                    } else {
                        setTotalRecords(0);
                    }
                } else {
                    setData([]);
                    setTotalRecords(0);
                }
            } else {
                setData([]);
                setTotalRecords(0);
            }
            setHasFetched(true);
        } catch (error) {
            console.error('Error fetching ledger data:', error);
            setData([]);
            setTotalRecords(0);
        } finally {
            setLoading(false);
        }
    }, [userId, TokenId, searchTerm, selectedCurrencyId, currentPage, itemsPerPage]);


    useEffect(() => {
        fetchCurrencies();
    }, [fetchCurrencies]);

    useEffect(() => {
        fetchLedgerData();
    }, [fetchLedgerData, currentPage, itemsPerPage]);

    const handleSearch = (e) => {
        if (e.key === 'Enter') {
            fetchLedgerData();
        }
    }

    const handleExport = () => {
        if (viewType !== 'table') {
            alert('Please switch to the table view to export data.');
            return;
        }

        // Polyfill for jQuery 4.0.0+ removing $.isArray
        if (!$.isArray) {
            $.isArray = Array.isArray;
        }

        const table = document.querySelector('table');
        if (table && $.fn.table2excel) {
            $(table).table2excel({
                exclude: ".noExl",
                name: "Customer Ledger",
                filename: "Customer_Ledger_Report_" + new Date().toISOString().split('T')[0],
                fileext: ".xls",
                exclude_img: true,
                exclude_links: true,
                exclude_inputs: true
            });
        } else {
            console.warn('table2excel plugin not loaded or table not found.');
            alert('Export failed. Plugin not loaded or table not found.');
        }
    };

    if (!canView) {
        return <PermissionDeniedComponent />;
    }

    return (
        <Box sx={{ p: 6, display: 'flex', flexDirection: 'column', gap: 4, height: '100%' }}>
            
            <SubHeader
                title={
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                        Customer Ledger
                        <Box sx={{ minWidth: 200 }}>
                            <TextField
                                select
                                fullWidth
                                value={selectedCurrencyId}
                                onChange={(e) => setSelectedCurrencyId(e.target.value)}
                                variant="outlined"
                                size="small"
                                placeholder="Choose Currency"
                                sx={{ 
                                    backgroundColor: '#fff', 
                                    borderRadius: '8px',
                                    '& .MuiInputBase-root': { height: '36px' }
                                }}
                            >
                                <MenuItem value={0}>All Currencies</MenuItem>
                                {currencies.map((curr) => (
                                    <MenuItem key={curr.currencyid} value={curr.currencyid}>
                                        {curr.currencyName} - {curr.currencyCode}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Box>
                    </Box>
                }
                totalCount={totalRecords}
                searchQuery={searchTerm}
                setSearchQuery={setSearchTerm}
                searchPlaceholder="Type to search Entity Detail..."
                onSearchClick={canSearch ? fetchLedgerData : undefined}
                viewMode={viewType}
                setViewMode={setViewType}
                showCalendar={false}
                onExportClick={canExport ? handleExport : undefined}
                showExport={canExport}
                pageName="Customer Ledger"
                pageCode="PAG10274"
                rightContent={
                    <Typography variant="body2" sx={{
                        backgroundColor: '#0ea5e9',
                        color: '#fff',
                        px: 3,
                        py: 1,
                        borderRadius: '4px',
                        fontWeight: 600,
                        ml: 2,
                        whiteSpace: 'nowrap'
                    }}>
                        Showing {data.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0} to {Math.min(currentPage * itemsPerPage, totalRecords)} of {totalRecords}
                    </Typography>
                }
            />

            {/* Content Section */}
            <Box sx={{ position: 'relative', flex: 1, minHeight: '400px' }}>
                <PremiumModuleWrapper
                    moduleName="Customer Ledger"
                    showCards={false}
                    showTable={false}
                    loading={loading}
                    data={data}
                    totalRecords={totalRecords}
                    currentPage={currentPage}
                    itemsPerPage={itemsPerPage}
                    onPageChange={setCurrentPage}
                    hasFetched={hasFetched}
                >
                    {viewType === 'table' ? (
                        <CustomerLedgerTable
                            data={data}
                            totalRecords={totalRecords}
                            currentPage={currentPage}
                            itemsPerPage={itemsPerPage}
                            onPageChange={setCurrentPage}
                            loading={loading}
                        />
                    ) : (
                        <CustomerLedgerCard data={data} loading={loading} />
                    )}
                </PremiumModuleWrapper>
            </Box>
        </Box>
    )
}

export default CustomerLedgerWrapper
